/**
 * OnboardingWizard.tsx — 3 étapes pour créer un nouveau template.
 * Porté depuis snapbooth-tempalte/project/onboarding.jsx.
 *
 * Step 1 — Choix du format (6 cards : 4×6, 2×6, 6×4, 1080², 9:16, custom)
 * Step 2 — Type d'évènement + nom + date + lieu + nb photos
 * Step 3 — Style visuel (6 cards) + bouton IA
 *
 * À l'`onFinish`, on transmet les choix au parent (le dashboard) qui peut
 * appliquer le format au store et naviguer vers /editor.
 */
import React, { useEffect, useState } from 'react';
import { X, Check, ChevronLeft, ChevronRight, Sparkles } from 'lucide-react';

export interface OnboardingChoice {
  format: string;
  eventType: string;
  eventName: string;
  eventDate: string;
  eventVenue: string;
  shotsPerSession: number;
  style: string;
}

export interface OnboardingWizardProps {
  open: boolean;
  onClose: () => void;
  onFinish?: (choice: OnboardingChoice) => void;
}

interface FormatOption {
  id: string;
  name: string;
  sub: string;
  ratio: string;
  popular?: boolean;
  custom?: boolean;
}
const FORMATS: FormatOption[] = [
  { id: '4x6', name: '4 × 6"', sub: 'Standard photobooth · portrait', ratio: '4/6', popular: true },
  { id: '2x6', name: '2 × 6"', sub: 'Strip classique · 3 ou 4 photos', ratio: '2/6' },
  { id: '6x4', name: '6 × 4"', sub: 'Paysage · idéal photos de groupe', ratio: '6/4' },
  { id: '1x1', name: '1080²', sub: 'Carré pour Instagram', ratio: '1/1' },
  { id: '9x16', name: '9 : 16', sub: 'Stories Instagram / TikTok', ratio: '9/16' },
  { id: 'custom', name: 'Personnalisé', sub: 'Vos dimensions', ratio: '4/6', custom: true },
];

const EVENT_TYPES = [
  { id: 'wedding', label: 'Mariage', emoji: '💍', color: '#D4AF37' },
  { id: 'birthday', label: 'Anniversaire', emoji: '🎂', color: '#EC4899' },
  { id: 'corporate', label: 'Entreprise', emoji: '🏢', color: '#4F46E5' },
  { id: 'baby', label: 'Baby shower', emoji: '🍼', color: '#A5B4FC' },
  { id: 'party', label: 'Soirée', emoji: '🎉', color: '#10B981' },
  { id: 'other', label: 'Autre', emoji: '✨', color: '#7C3AED' },
] as const;

const STYLES = [
  {
    id: 'elegant',
    name: 'Élégant Or',
    sub: 'Playfair · or · classique',
    label: 'Léa & Tom',
    bg: 'linear-gradient(160deg, #D4AF37, #B8860B 60%, #8B6914)',
  },
  {
    id: 'bohemian',
    name: 'Bohème',
    sub: 'Tons terre · script · floral',
    label: 'Amour vrai',
    bg: 'linear-gradient(160deg, #C19A6B, #8B6F47 60%, #5C4830)',
  },
  {
    id: 'modern',
    name: 'Moderne minimaliste',
    sub: 'Sans-serif · noir & blanc',
    label: 'AURA',
    bg: 'linear-gradient(160deg, #2A2440, #1A1530 60%, #0F0823)',
  },
  {
    id: 'fun',
    name: 'Festif coloré',
    sub: 'Confetti · couleurs vives',
    label: 'Yeahhh !',
    bg: 'linear-gradient(160deg, #F472B6, #EC4899 60%, #BE185D)',
  },
  {
    id: 'neon',
    name: 'Néon nuit',
    sub: 'Dark · accents fluo',
    label: 'Nightlife',
    bg: 'linear-gradient(160deg, #10B981, #059669 60%, #064E3B)',
  },
  {
    id: 'vintage',
    name: 'Vintage Polaroid',
    sub: 'Sépia · grain · cadres',
    label: 'Souvenir',
    bg: 'linear-gradient(160deg, #C9B98A, #9D8B6B 60%, #6B5D40)',
  },
] as const;

export const OnboardingWizard: React.FC<OnboardingWizardProps> = ({ open, onClose, onFinish }) => {
  const [step, setStep] = useState(0);
  const [format, setFormat] = useState<string>('4x6');
  const [eventType, setEventType] = useState<string>('wedding');
  const [eventName, setEventName] = useState<string>('Mariage Léa & Tom');
  const [eventDate, setEventDate] = useState<string>('2026-06-14');
  const [eventVenue, setEventVenue] = useState<string>('');
  const [shotsPerSession, setShots] = useState<number>(4);
  const [style, setStyle] = useState<string>('elegant');

  useEffect(() => {
    if (open) setStep(0);
  }, [open]);

  // Fermer avec Échap
  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [open, onClose]);

  const finish = () => {
    onFinish?.({ format, eventType, eventName, eventDate, eventVenue, shotsPerSession, style });
    onClose();
  };

  if (!open) return null;

  return (
    <div
      className="ob-overlay"
      onClick={e => {
        if (e.target === e.currentTarget) onClose();
      }}
    >
      <div
        className="ob-shell"
        role="dialog"
        aria-label="Création d'un nouveau template"
        aria-modal="true"
      >
        {/* Header avec progress */}
        <div className="ob-head">
          <div className="ob-progress" aria-label="Progression">
            {(['Format', 'Évènement', 'Style'] as const).map((label, i) => (
              <React.Fragment key={label}>
                <div className={`ob-step-pill ${i === step ? 'current' : ''}`}>
                  <div
                    className={`ob-step-dot ${i < step ? 'done' : ''} ${i === step ? 'current' : ''}`}
                  >
                    {i < step ? <Check size={10} /> : i + 1}
                  </div>
                  <span>{label}</span>
                </div>
                {i < 2 && <div className={`ob-step-line ${i < step ? 'done' : ''}`} />}
              </React.Fragment>
            ))}
          </div>
          <button className="ob-close" onClick={onClose} aria-label="Fermer">
            <X size={18} />
          </button>
        </div>

        {/* Body */}
        <div className="ob-body">
          {step === 0 && <StepFormat format={format} setFormat={setFormat} />}
          {step === 1 && (
            <StepEvent
              eventType={eventType}
              setEventType={setEventType}
              eventName={eventName}
              setEventName={setEventName}
              eventDate={eventDate}
              setEventDate={setEventDate}
              eventVenue={eventVenue}
              setEventVenue={setEventVenue}
              shotsPerSession={shotsPerSession}
              setShots={setShots}
            />
          )}
          {step === 2 && <StepStyle style={style} setStyle={setStyle} />}
        </div>

        {/* Footer */}
        <div className="ob-foot">
          <button className="sb-btn sb-btn-ghost" onClick={onClose}>
            Passer
          </button>
          <div style={{ display: 'flex', gap: 8 }}>
            {step > 0 && (
              <button className="sb-btn sb-btn-secondary" onClick={() => setStep(step - 1)}>
                <ChevronLeft size={14} /> Précédent
              </button>
            )}
            {step < 2 ? (
              <button className="sb-btn sb-btn-primary sb-btn-lg" onClick={() => setStep(step + 1)}>
                Continuer <ChevronRight size={14} />
              </button>
            ) : (
              <button className="sb-btn sb-btn-primary sb-btn-lg" onClick={finish}>
                <Sparkles size={14} /> Créer mon template
              </button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

/* ─── Step 1 ─── */
const StepFormat: React.FC<{ format: string; setFormat: (s: string) => void }> = ({
  format,
  setFormat,
}) => (
  <div className="ob-step">
    <div className="ob-step-emoji" aria-hidden="true">
      📐
    </div>
    <h2 className="ob-step-title">Choisissez un format</h2>
    <p className="ob-step-sub">
      Sur quel format vos invités repartiront-ils ? Vous pourrez toujours en changer.
    </p>
    <div className="ob-format-grid">
      {FORMATS.map(f => (
        <button
          key={f.id}
          className={`ob-format ${format === f.id ? 'is-active' : ''} ${f.custom ? 'is-custom' : ''}`}
          onClick={() => setFormat(f.id)}
        >
          {f.popular && <div className="ob-format-badge">Populaire</div>}
          <div className="ob-format-preview" style={{ aspectRatio: f.ratio }} />
          <div className="ob-format-name">{f.name}</div>
          <div className="ob-format-sub">{f.sub}</div>
        </button>
      ))}
    </div>
  </div>
);

/* ─── Step 2 ─── */
interface StepEventProps {
  eventType: string;
  setEventType: (s: string) => void;
  eventName: string;
  setEventName: (s: string) => void;
  eventDate: string;
  setEventDate: (s: string) => void;
  eventVenue: string;
  setEventVenue: (s: string) => void;
  shotsPerSession: number;
  setShots: (n: number) => void;
}
const StepEvent: React.FC<StepEventProps> = ({
  eventType,
  setEventType,
  eventName,
  setEventName,
  eventDate,
  setEventDate,
  eventVenue,
  setEventVenue,
  shotsPerSession,
  setShots,
}) => (
  <div className="ob-step">
    <div className="ob-step-emoji" aria-hidden="true">
      🎊
    </div>
    <h2 className="ob-step-title">Parlez-nous de l'évènement</h2>
    <p className="ob-step-sub">Ces infos seront remplacées automatiquement sur vos tirages.</p>

    <div className="ob-event-types">
      {EVENT_TYPES.map(t => (
        <button
          key={t.id}
          className={`ob-event-type ${eventType === t.id ? 'is-active' : ''}`}
          onClick={() => setEventType(t.id)}
          style={
            eventType === t.id ? { borderColor: t.color, background: `${t.color}15` } : undefined
          }
        >
          <span className="ob-event-emoji" style={{ background: t.color }}>
            {t.emoji}
          </span>
          <span>{t.label}</span>
        </button>
      ))}
    </div>

    <div className="ob-field-grid">
      <div className="ob-field">
        <div className="ob-field-label">Nom de l'évènement</div>
        <input
          className="ob-input"
          value={eventName}
          onChange={e => setEventName(e.target.value)}
          placeholder="ex. Mariage Léa & Tom"
        />
        <div className="ob-helper">Apparaîtra sur le tirage final</div>
      </div>
      <div className="ob-field">
        <div className="ob-field-label">Date</div>
        <input
          className="ob-input"
          type="date"
          value={eventDate}
          onChange={e => setEventDate(e.target.value)}
        />
      </div>
      <div className="ob-field">
        <div className="ob-field-label">Lieu (optionnel)</div>
        <input
          className="ob-input"
          value={eventVenue}
          onChange={e => setEventVenue(e.target.value)}
          placeholder="ex. Domaine de Verdière"
        />
      </div>
      <div className="ob-field">
        <div className="ob-field-label">Nb de photos par session</div>
        <div className="ob-segmented">
          {[1, 2, 3, 4].map(n => (
            <button
              key={n}
              className={n === shotsPerSession ? 'active' : ''}
              onClick={() => setShots(n)}
            >
              {n}
            </button>
          ))}
        </div>
      </div>
    </div>
  </div>
);

/* ─── Step 3 ─── */
const StepStyle: React.FC<{ style: string; setStyle: (s: string) => void }> = ({
  style,
  setStyle,
}) => (
  <div className="ob-step">
    <div className="ob-step-emoji" aria-hidden="true">
      🎨
    </div>
    <h2 className="ob-step-title">Et le style ?</h2>
    <p className="ob-step-sub">Choisissez l'ambiance — vous pourrez tout personnaliser ensuite.</p>

    <div className="ob-style-grid">
      {STYLES.map(s => (
        <button
          key={s.id}
          className={`ob-style ${style === s.id ? 'is-active' : ''}`}
          onClick={() => setStyle(s.id)}
        >
          <div className="ob-style-preview" style={{ background: s.bg }}>
            {s.label}
            {style === s.id && (
              <div className="ob-style-selected">
                <Check size={14} />
              </div>
            )}
          </div>
          <div className="ob-style-name">{s.name}</div>
          <div className="ob-style-sub">{s.sub}</div>
        </button>
      ))}
    </div>

    <div className="ob-ai-banner">
      <div className="ob-ai-glyph">
        <Sparkles size={20} />
      </div>
      <div style={{ flex: 1 }}>
        <div className="ob-ai-title">Ou laissez l'IA proposer un style adapté</div>
        <div className="ob-ai-sub">
          Snapbooth IA analyse votre événement et génère 3 propositions personnalisées
        </div>
      </div>
      <button className="sb-btn sb-btn-secondary sb-btn-sm">
        <Sparkles size={13} /> Lancer l'IA
      </button>
    </div>
  </div>
);
